import { useReportDetailStore } from "./store";
import { RelacoesSectionListItem, SectionListRecordsItem, UpdateFnPromise } from "./interface";
import { ReportMetadata, ReportSection, SummaryData, SummaryStatus } from "~/types/global";
import { REPORT_DETAIL_INITIAL_STATE } from "./state";
import { REPORT_SECTIONS } from "~/helpers/constants";
import { getProfileImageFromSection } from "~/helpers";

const getStore = () => useReportDetailStore;

const calculateSectionListRecords = (sections: ReportSection[]): SectionListRecordsItem[] => {
  return sections
    .filter(section => {
      if (section.subsection) return false;
      if (section.is_deleted) return false;
      if (section.data_count > 0) return true;

      // Algumas seções vazias vem com a chave "detalhes" vazia dentro de "data", por isso a verificação abaixo
      return Array.isArray(section.data) &&
        section.data.some((item) =>
          Array.isArray((item as any).detalhes) &&
          (item as any).detalhes.length > 0
        );
    })
    .map((s) => {
      const originalIndex = sections.indexOf(s);
      return {
        title: s.title,
        data_count: s.data_count,
        index: originalIndex,
        interface_id: s.interface_id
      };
    });
};

const calculateTotalRecords = (sectionListRecords: SectionListRecordsItem[]): number => {
  return sectionListRecords.reduce((sum, section) => {
    const count = Number(section.data_count) || 0;
    return sum + count;
  }, 0);
};

const calculateRelacoesSectionList = (sections: ReportSection[]): RelacoesSectionListItem[] => {
  const relacoesSectionList: RelacoesSectionListItem[] = [];
  const relacoesSection = sections.find(section => section.title === "Relações");
  if (relacoesSection && Array.isArray(relacoesSection.data)) {
    const ordinalLabels = [
      "primeiro vínculo",
      "segundo vínculo",
      "terceiro vínculo",
      "quarto vínculo",
      "quinto vínculo",
      "sexto vínculo",
      "sétimo vínculo",
      "oitavo vínculo",
      "nono vínculo",
      "décimo vínculo"
    ];

    relacoesSection.data.forEach((item, index) => {
      if (index < ordinalLabels.length) {
        relacoesSectionList.push({
          title: ordinalLabels[index],
          data_count: 1,
          index: index,
          interface_id: `relacoes-${index}-${relacoesSection.interface_id || 'fallback'}`
        });
      }
    });
  }
  return relacoesSectionList;
};

export const updateSectionListRecords = () => {
  const store = getStore().getState();
  const sections = store.data.sections;

  const sectionListRecords = calculateSectionListRecords(sections);
  const totalRecords = calculateTotalRecords(sectionListRecords);
  const relacoesSectionList = calculateRelacoesSectionList(sections);

  store.actions.setSectionListRecords(sectionListRecords);
  store.actions.setTotalRecords(totalRecords);
  store.actions.setRelacoesSectionList(relacoesSectionList);
};

export const setReportSections = (sections: ReportSection[]) => {
  const useReportDetailStore = getStore();
  const store = useReportDetailStore.getState();

  // Extrair imagem de perfil da seção imagens
  const imagensSection = sections.find(
    (section) => section.title === REPORT_SECTIONS.imagens
  );
  const profileImage = getProfileImageFromSection(imagensSection);

  // Calcular todos os valores relacionados às seções usando os helpers
  const sectionListRecords = calculateSectionListRecords(sections);
  const totalRecords = calculateTotalRecords(sectionListRecords);
  const relacoesSectionList = calculateRelacoesSectionList(sections);

  store.actions.setReportSections(sections);
  store.actions.setProfileImage(profileImage);
  store.actions.setSectionListRecords(sectionListRecords);
  store.actions.setRelacoesSectionList(relacoesSectionList);
  store.actions.setTotalRecords(totalRecords);
};

export const setDeletedSections = (deletedSections: ReportSection[]) => {
  getStore().getState().actions.setDeletedSections(deletedSections);
};

export const setReportMetadata = (metadata: ReportMetadata) => {
  getStore().getState().actions.setMetadata(metadata);
};

export const setReportType = (reportType: string) => {
  getStore().getState().actions.setReportType(reportType);
};

export const setSelectedCombinedFilter = (filterId: string) => {
  getStore().getState().actions.setSelectedCombinedFilter(filterId);
};

export const setCombinedReportSources = (sources: Array<{
  report_type: string;
  report_input_value: string | Record<string, any>;
  user_reports_id: string;
  report_name: string;
}>) => {
  getStore().getState().actions.setCombinedReportSources(sources);
};

export const setProfileImage = (profileImage: string) => {
  getStore().getState().actions.setProfileImage(profileImage);
};

export const forceReload = () => {
  getStore().getState().actions.forceReload();
};

export const updateSectionEntries = (
  sectionTitle: string,
  updaterFn: UpdateFnPromise,
  testEntryDeletedFn: (entry: ReportSection["data"][0]) => boolean,
  testSectionDeletedFn: (section: ReportSection) => boolean,
  calculateDataCountFn?: (section: ReportSection) => number,
  includeSubsections?: boolean,
  crossSectionUpdate?: { matchingProp: string; updaterFn: UpdateFnPromise }
) => {
  getStore().getState().actions.updateSectionEntries(
    sectionTitle,
    updaterFn,
    testEntryDeletedFn,
    testSectionDeletedFn,
    calculateDataCountFn,
    includeSubsections,
    crossSectionUpdate
  );
};

export const updateSubsectionWithMainSection = (
  sectionTitle: string,
  subsectionName: string,
  matchingProp: string,
  updaterFn: UpdateFnPromise,
  testEntryDeletedFn: (entry: ReportSection["data"][0]) => boolean,
  testSectionDeletedFn: (section: ReportSection) => boolean,
  calculateDataCountFn?: (section: ReportSection) => number
) => {
  getStore().getState().actions.updateSubsectionWithMainSection(
    sectionTitle,
    subsectionName,
    matchingProp,
    updaterFn,
    testEntryDeletedFn,
    testSectionDeletedFn,
    calculateDataCountFn
  );
};

export const scheduleAutoSave = () => {
  getStore().getState().actions.scheduleAutoSave();
};

export const cancelAutoSave = () => {
  getStore().getState().actions.cancelAutoSave();
};

export const setAutoSaveMutation = (mutation: any) => {
  getStore().getState().actions.setAutoSaveMutation(mutation);
};

export const setAutoSaveEnabled = (enabled: boolean) => {
  getStore().getState().actions.setAutoSaveEnabled(enabled);
};

export const clearPendingChanges = () => {
  getStore().getState().actions.clearPendingChanges();
};

export const useActionLoading = () => {
  const useReportDetailStore = getStore();
  return useReportDetailStore((state) => state.ui.isActionLoading);
};

export const setActiveSection = (sectionKey: string | null) => {
  getStore().getState().actions.setActiveSection(sectionKey);
};

export const setScrollLoading = (loading: boolean) => {
  getStore().getState().actions.setScrollLoading(loading);
};

export const setAccordionToggle = (toggle: ((sectionTitle: string) => void) | null) => {
  getStore().getState().actions.setAccordionToggle(toggle);
};

export const getAccordionToggle = () => {
  return getStore().getState().ui.accordionToggle;
};

export const setPendingScrollTarget = (target: string | null) => {
  getStore().getState().actions.setPendingScrollTarget(target);
};

export const getPendingScrollTarget = () => {
  return getStore().getState().ui.pendingScrollTarget;
};

export const setOpenSections = (sections: string[]) => {
  getStore().getState().actions.setOpenSections(sections);
};

export const toggleOpenSection = (sectionId: string) => {
  getStore().getState().actions.toggleOpenSection(sectionId);
};

export const addOpenSection = (sectionId: string) => {
  getStore().getState().actions.addOpenSection(sectionId);
};

export const getOpenSections = () => {
  return getStore().getState().ui.openSections;
};

export const isSectionOpen = (sectionId: string) => {
  return getStore().getState().ui.openSections.includes(sectionId);
};

export const setOpenSectionsTrash = (sections: string[]) => {
  getStore().getState().actions.setOpenSectionsTrash(sections);
};

export const toggleOpenSectionTrash = (sectionId: string) => {
  getStore().getState().actions.toggleOpenSectionTrash(sectionId);
};

export const addOpenSectionTrash = (sectionId: string) => {
  getStore().getState().actions.addOpenSectionTrash(sectionId);
};

export const getOpenSectionsTrash = () => {
  return getStore().getState().ui.openSectionsTrash;
};

export const isSectionOpenTrash = (sectionId: string) => {
  return getStore().getState().ui.openSectionsTrash.includes(sectionId);
};

export const setActiveRelacaoTab = (tab: 'semactic-analysis' | 'relationship-flow') => {
  getStore().getState().actions.setActiveRelacaoTab(tab);
};

export const setHighlightedEntityId = (entityId: string | null) => {
  getStore().getState().actions.setHighlightedEntityId(entityId);
};

// Sincroniza highlight de entidade com seção ativa para relatórios de relações
export const setHighlightedEntityAndActiveSection = (entityIndex: number | null) => {
  const store = getStore().getState();

  console.log('[setHighlightedEntityAndActiveSection] Called with:', { entityIndex });

  if (entityIndex === null) {
    store.actions.setHighlightedEntityId(null);
    console.log('[setHighlightedEntityAndActiveSection] Cleared highlight');
    return;
  }

  const entityCardId = `entity-card-${entityIndex}`;
  store.actions.setHighlightedEntityId(entityCardId);

  // Encontrar o interface_id correspondente na lista de relações
  const relacoesList = store.ui.relacoesSectionList;
  const matchingRelation = relacoesList.find(item => item.index === entityIndex);

  console.log('[setHighlightedEntityAndActiveSection] Found matching relation:', {
    entityCardId,
    relacoesList,
    matchingRelation,
    willSetActiveSection: matchingRelation?.interface_id
  });

  if (matchingRelation?.interface_id) {
    store.actions.setActiveSection(matchingRelation.interface_id);
    console.log('[setHighlightedEntityAndActiveSection] Set active section to:', matchingRelation.interface_id);
  }
};

export const resetReportDetailStore = () => {
  getStore().setState({
    ...REPORT_DETAIL_INITIAL_STATE,
  });
};

export const resetRelacoesState = () => {
  const store = getStore().getState();
  store.actions.resetRelacoesState();
};

export const setRefetchReport = (refetch: (() => Promise<any>) | null) => {
  getStore().getState().actions.setRefetchReport(refetch);
};

export const setSummaryStatus = (status: SummaryStatus) => {
  getStore().getState().actions.setSummaryStatus(status);
};

export const setSummaryData = (data: SummaryData | null) => {
  getStore().getState().actions.setSummaryData(data);
};

export const setIsGeneratingSummary = (isGenerating: boolean) => {
  getStore().getState().actions.setIsGeneratingSummary(isGenerating);
};